<template>
  <div class="flex shrink-0 flex-col md:items-end">
    <div
      class="flex"
      :class="[
        {
          'flex-col items-baseline md:flex-row': isDefaultLayout,
          'shrink-0 flex-col text-right': isVerticalLayout,
        },
      ]"
    >
      <RevPopover
        v-if="shouldDisplayOriginalPrice"
        class="z-[1]"
        position="bottom"
      >
        <template #trigger="{ hide, show }">
          <button
            class="body-1 cursor-pointer line-through"
            :class="[
              {
                'mr-8': isDefaultLayout,
              },
            ]"
            @click="show"
            @focusin="show"
            @focusout="hide"
            @mouseenter="show"
            @mouseleave="hide"
          >
            {{ originalPrice }}
          </button>
        </template>
        <template #content="{ position }">
          <RevTooltip class="mx-12" :position="position">
            <span class="body-2">
              {{ i18n(translations.strikedPriceExplanation) }}
            </span>
          </RevTooltip>
        </template>
      </RevPopover>

      <div
        class="flex"
        :class="[
          {
            'items-baseline md:flex-col md:items-end': isDefaultLayout,
            'flex-col items-end': isVerticalLayout,
          },
        ]"
      >
        <ClientOnly>
          <h2
            class="heading-2 md:text-right"
            :class="color"
            data-qa="productpage-product-price"
          >
            {{ i18n.price(offer.price) }}
          </h2>

          <template #fallback>
            <RevSkeleton height="32px" shape="rectangle" width="80px" />
          </template>
        </ClientOnly>

        <span v-if="shouldDisplayTradeIn" class="caption pl-4 md:pl-0">
          {{ i18n(translations.beforeTradeIn) }}
        </span>
      </div>
    </div>

    <span
      v-if="shouldDisplayVatIncluded"
      class="caption"
      :class="[{ 'text-right': isVerticalLayout }]"
    >
      {{ i18n(translations.productVatIncluded) }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { type Price, productAPI } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevPopover } from '@ds/components/Popover'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevTooltip } from '@ds/components/Tooltip'

import {
  COUNTRIES_WITHOUT_ORIGINAL_PRICE,
  COUNTRIES_WITH_TRADE_IN_PRICE_LABEL,
} from './Price.constants'
import translations from './Price.translations'

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    color?: string
    offer: productAPI.GetBestOffersResponse[number]
    product: productAPI.GetProductResponse
    layout?: 'default' | 'vertical'
  }>(),
  {
    color: '',
    layout: 'default',
  },
)

const {
  market: { countryCode },
  features: { vatIncluded },
} = useMarketplace()

const shouldDisplayOriginalPrice = computed(() => {
  return (
    props.offer.reassurance?.discount?.isDisplayed &&
    !COUNTRIES_WITHOUT_ORIGINAL_PRICE.includes(countryCode) &&
    !isEmpty(props.product.priceWhenNew)
  )
})

const originalPrice = computed(() => {
  if (shouldDisplayOriginalPrice.value) {
    return i18n(translations.originalPriceDisplay, {
      price: i18n.price(props.product.priceWhenNew as Price),
    })
  }

  return null
})

const shouldDisplayTradeIn = computed(() => {
  return COUNTRIES_WITH_TRADE_IN_PRICE_LABEL.includes(countryCode)
})

const shouldDisplayVatIncluded = computed(() => {
  return vatIncluded && !shouldDisplayTradeIn.value
})

const isDefaultLayout = computed(() => {
  return props.layout === 'default'
})

const isVerticalLayout = computed(() => {
  return props.layout === 'vertical'
})
</script>
